/**
 * Legal Page — renders full legal documents (oferta, privacy policy).
 * Reusable renderer for structured LegalDocument data.
 */

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  TERMS_UZ, TERMS_EN, PRIVACY_UZ, PRIVACY_EN,
  type LegalDocument, type LegalSection,
} from '../data/legal-content'

type LegalMode = 'terms' | 'privacy'

interface LegalPageProps {
  isOpen: boolean
  onClose: () => void
  mode: LegalMode
}

function useLegalDocument(mode: LegalMode): LegalDocument {
  const { i18n } = useTranslation()
  const isUz = i18n.language === 'uz'

  if (mode === 'terms') return isUz ? TERMS_UZ : TERMS_EN
  return isUz ? PRIVACY_UZ : PRIVACY_EN
}

export const LegalPage: React.FC<LegalPageProps> = ({ isOpen, onClose, mode }) => {
  const doc = useLegalDocument(mode)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const [isScrolled, setIsScrolled] = React.useState(false)

  const handleScroll = React.useCallback((): void => {
    if (!scrollRef.current) return
    setIsScrolled(scrollRef.current.scrollTop > 200)
  }, [])

  const scrollToTop = React.useCallback((): void => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          <motion.div
            initial={{ y: '100%', opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0.8 }}
            transition={{ type: 'tween', duration: 0.25, ease: 'easeInOut' }}
            className="fixed z-50 bg-bg flex flex-col
              inset-x-0 bottom-0 rounded-t-[32px] max-h-[92vh]
              lg:inset-auto lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2
              lg:rounded-3xl lg:max-h-[85vh] lg:w-full lg:max-w-lg
              lg:border lg:border-white/[0.06] lg:shadow-glass"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0 border-b border-white/[0.06]">
              <button
                onClick={onClose}
                className="w-10 h-10 flex items-center justify-center rounded-full
                  hover:bg-white/[0.04] tap-highlight-transparent"
              >
                <X size={20} className="text-text" />
              </button>
              <h2 className="font-display font-bold text-lg text-text">{doc.title}</h2>
              <div className="w-10" />
            </div>

            {/* Content */}
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto px-5 pb-10"
            >
              {/* Document header */}
              <div className="py-5 text-center border-b border-white/[0.04] mb-5">
                <p className="text-xs text-text-hint/60 uppercase tracking-wider">
                  {doc.subtitle}
                </p>
                <p className="text-xs text-text-hint/40 mt-1">{doc.effectiveDate}</p>
              </div>

              {/* Preamble */}
              <p className="text-sm text-text/70 leading-relaxed mb-6">
                {doc.preamble}
              </p>

              {/* Sections */}
              {doc.sections.map((section) => (
                <SectionBlock key={section.id} section={section} />
              ))}

              {/* Requisites */}
              <div className="mt-8 pt-5 border-t border-white/[0.06]">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-text-hint/60 mb-3">
                  {mode === 'terms' ? 'Rekvizitlar' : 'Aloqa ma\'lumotlari'}
                </h3>
                <div className="text-xs text-text/50 leading-relaxed space-y-0.5">
                  {doc.requisites.map((line, i) => (
                    <p key={i}>{line || '\u00A0'}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Scroll to top button */}
            <AnimatePresence>
              {isScrolled && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  onClick={scrollToTop}
                  className="absolute bottom-6 right-5 w-10 h-10 rounded-full
                    bg-primary/90 flex items-center justify-center shadow-lg
                    tap-highlight-transparent z-10"
                >
                  <ChevronUp size={18} className="text-white" />
                </motion.button>
              )}
            </AnimatePresence>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

/* ── Section renderer ──────────────────────────────────────── */

const SectionBlock: React.FC<{ section: LegalSection }> = React.memo(({ section }) => (
  <div className="mb-5">
    <h3 className="text-sm font-display font-bold text-text mb-2">
      {section.title}
    </h3>
    <div className="space-y-2">
      {section.paragraphs.map((p, i) => (
        <ParagraphBlock key={i} text={p} />
      ))}
    </div>
  </div>
))
SectionBlock.displayName = 'SectionBlock'

/* ── Paragraph renderer (handles \n for lists) ─────────────── */

const ParagraphBlock: React.FC<{ text: string }> = React.memo(({ text }) => {
  if (text.includes('\n')) {
    const lines = text.split('\n')
    return (
      <div className="text-[13px] text-text/60 leading-relaxed">
        {lines.map((line, i) => (
          <p key={i} className={line.startsWith('•') ? 'pl-3' : ''}>
            {line}
          </p>
        ))}
      </div>
    )
  }

  return (
    <p className="text-[13px] text-text/60 leading-relaxed">{text}</p>
  )
})
ParagraphBlock.displayName = 'ParagraphBlock'
